// File: team-v2/components/dashboard/TeamProgressUI.js

import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useTeamProgressTracker from '../../hooks/useTeamProgressTracker';
import { useAuth } from 'contexts/AuthContext';
import { useTeam } from 'team/contexts/teamContext';
import TeamProgressBox from './TeamProgressBox';
import { ArrowBackIos, ArrowForwardIos } from 'icons/materialIcons';
// Team progress configuration (moved from data folder to match individual plan structure)
const teamProgressSteps = [
    {
        title: 'Create your team',
        description: 'Set company info',
        completed: true,
        onClick: () => {
            // Navigate to team creation page
            window.location.href = '/team-v2/members';
        }
    },
    {
        title: 'Add company logo and name',
        description: 'Add a company logo and name to personalize your invite emails and more.',
        completed: false,
        onClick: () => {
            // Navigate to company settings page
            window.location.href = '/team-v2/settings';
        }
    },
    {
        title: 'Add members to your team',
        description: 'You selected a team size of 6 - 20. Add those members here.',
        completed: false,
        onClick: () => {
            // Navigate to team members page
            window.location.href = '/team-v2/members';
        }
    },
    {
        title: 'Create and assign a template',
        description: 'Use templates to easily create cards for your team members in bulk.',
        completed: false,
        onClick: () => {
            // Navigate to templates page
            window.location.href = '/team-v2/templates';
        }
    },
    {
        title: 'Set up your CRM Integration',
        description: 'Sync your Touch leads to your CRM so your team can take action on them.',
        completed: false,
        onClick: () => {
            // Navigate to integrations page
            window.location.href = '/team-v2/integrations';
        }
    }
];

const teamProgressConfig = {
    title: 'Set up your Touch Team',
    subtitle: 'Complete these steps for the best Team experience.',
    maxSteps: 5,
    completedMessage: 'Congrats, your Touch team is all set up!',
    completedSubtitle: 'Your Touch team is now ready for collaboration and growth.'
};

// Gradient border styles (copied from individual plan)
const createGradientBorderStyles = (borderRadius = '12px') => ({
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: borderRadius,
        border: '1px solid transparent',
        background: 'linear-gradient(to bottom right, #36dad6, #685ed1) border-box',
        WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'destination-out',
        mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude',
        pointerEvents: 'none'
    }
});

const TeamProgressUI = () => {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(true);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const scrollContainerRef = useRef(null);

    // Add state to prevent multiple simultaneous progress checks
    const [isCheckingProgress, setIsCheckingProgress] = useState(false);
    const lastCheckTimeRef = useRef(0);
    const DEBOUNCE_DELAY = 2000; // 2 seconds between checks

    const {
        createTeam,
        addCompanyLogo,
        addMembers,
        createTemplate,
        setupCrmIntegration,
        completedSteps,
        updateAllProgress
    } = useTeamProgressTracker();

    const { currentTeamId } = useTeam();
    const { authState } = useAuth();

    // Check if we're in signup flow
    const isSignupFlowActive = localStorage.getItem('signupInProgress') === 'true';
    const isAuthenticated = authState?.isAuthenticated;

    // Enhanced progress checking with proper guards and debouncing
    useEffect(() => {
        const checkProgress = async () => {
            if (!currentTeamId || isCheckingProgress) return;

            const now = Date.now();
            if (now - lastCheckTimeRef.current < DEBOUNCE_DELAY) return;

            lastCheckTimeRef.current = now;
            setIsCheckingProgress(true);

            try {
                await updateAllProgress(currentTeamId);
            } catch (error) {
                console.error('Error checking team progress:', error);
            } finally {
                setIsCheckingProgress(false);
            }
        };

        checkProgress();
    }, [currentTeamId, isCheckingProgress, updateAllProgress]);

    // Add scroll event listener to update scroll position
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollLeft = container.scrollLeft;
            const maxScroll = container.scrollWidth - container.clientWidth;
            const canScroll = scrollLeft < maxScroll - 10; // 10px tolerance

            console.log('Scroll Debug:', {
                scrollLeft,
                maxScroll,
                canScroll,
                scrollWidth: container.scrollWidth,
                clientWidth: container.clientWidth
            });

            setScrollPosition(scrollLeft);
            setCanScrollRight(canScroll);
        };

        container.addEventListener('scroll', handleScroll);
        // Initial check
        handleScroll();
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll functions
    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const scrollAmount = 300; // Scroll by 300px
            const newPosition = Math.max(0, scrollPosition - scrollAmount);
            container.scrollTo({ left: newPosition, behavior: 'smooth' });
            setScrollPosition(newPosition);
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const scrollAmount = 300; // Scroll by 300px
            const maxScroll = container.scrollWidth - container.clientWidth;
            const newPosition = Math.min(maxScroll, scrollPosition + scrollAmount);
            container.scrollTo({ left: newPosition, behavior: 'smooth' });
            setScrollPosition(newPosition);
        }
    };

    // Check if all steps are completed
    const allStepsCompleted = createTeam && addCompanyLogo && addMembers && createTemplate && setupCrmIntegration;

    const progressStepStyle = (completed) => ({
        width: '110px',
        height: '8px',
        backgroundColor: completed ? '#29aef8' : '#e0e0e0',
        border: `1px solid ${completed ? '#2499da' : '#c5c5c5'}`,
        borderRadius: '5px',
    });

    // Create progress steps with actual completion status and different widths
    const progressSteps = teamProgressSteps.map((step, index) => {
        let completed = false;
        switch (index) {
            case 0:
                completed = true; // Force first step to always be completed
                break;
            case 1:
                completed = addCompanyLogo;
                break;
            case 2:
                completed = addMembers;
                break;
            case 3:
                completed = createTemplate;
                break;
            case 4:
                completed = setupCrmIntegration;
                break;
            default:
                completed = false;
        }

        return {
            ...step,
            completed,
            width: completed ? '200px' : '300px', // Completed boxes smaller, non-completed boxes larger
            onClick: () => {
                // Direct navigation based on step index
                switch (index) {
                    case 0:
                        navigate('/team-v2/members');
                        break;
                    case 1:
                        navigate('/team-v2/settings');
                        break;
                    case 2:
                        navigate('/team-v2/members');
                        break;
                    case 3:
                        navigate('/team-v2/templates');
                        break;
                    case 4:
                        navigate('/team-v2/integrations');
                        break;
                    default:
                        break;
                }
            }
        };
    });

    // Don't render during signup flow or if not authenticated
    console.log('🔍 TeamProgressUI: Render check:', {
        isSignupFlowActive,
        isAuthenticated,
        currentTeamId: currentTeamId ? currentTeamId.substring(0, 10) + '...' : 'null',
        signupInProgress: localStorage.getItem('signupInProgress'),
        currentPath: window.location.pathname
    });

    // Additional check: don't render if we're on signup-related paths
    const isOnSignupPath = window.location.pathname.includes('/sign-up') ||
        window.location.pathname.includes('/signup') ||
        window.location.pathname.includes('/sign_up');

    if (isSignupFlowActive || !isAuthenticated || isOnSignupPath) {
        console.log('🔍 TeamProgressUI: Not rendering - signup flow, not authenticated, or on signup path');
        return null;
    }

    if (!isVisible) {
        return null;
    }

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                backgroundColor: '#f5fcff',
                borderRadius: '12px',
                padding: allStepsCompleted ? '12px' : '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: allStepsCompleted ? '0px' : '16px',
                margin: '8px auto',
                marginBottom: allStepsCompleted ? '20px' : '8px',
                boxSizing: 'border-box',
                overflow: 'visible',
                ...createGradientBorderStyles('8px')
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                }}
            >
                <Box sx={{ flex: 1, marginBottom: { xs: '16px', sm: 0 } }}>
                    <Typography sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '18px',
                        fontWeight: 500,
                        color: '#000',
                        margin: 0,
                        lineHeight: '25.74px',
                        WebkitFontSmoothing: 'antialiased',
                        boxSizing: 'border-box',
                        cursor: 'default',
                        display: 'block',
                        height: '25.7375px',
                        width: '331.237px',
                        outline: 'none'
                    }}>
                        {allStepsCompleted ? teamProgressConfig.completedMessage : teamProgressConfig.title}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px',
                            fontWeight: 400,
                            color: '#000',
                            margin: '4px 0 0 0',
                            lineHeight: '17.16px',
                            WebkitFontSmoothing: 'antialiased',
                            boxSizing: 'border-box',
                            cursor: 'default',
                            display: 'block',
                            height: '17.15px',
                            width: '331.237px',
                            outline: 'none'
                        }}
                    >
                        {allStepsCompleted ? teamProgressConfig.completedSubtitle : teamProgressConfig.subtitle}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box sx={progressStepStyle(true)} />
                    <Box sx={progressStepStyle(addCompanyLogo)} />
                    <Box sx={progressStepStyle(addMembers)} />
                    <Box sx={progressStepStyle(createTemplate)} />
                    <Box sx={progressStepStyle(setupCrmIntegration)} />
                    <Typography variant="body2" sx={{ color: '#b0b0b0', marginLeft: '8px', marginRight: allStepsCompleted ? '16px' : '0' }}>
                        {completedSteps}/{teamProgressConfig.maxSteps} Completed
                    </Typography>

                    {allStepsCompleted && (
                        <Button
                            variant="contained"
                            onClick={() => setIsVisible(false)}
                            sx={{
                                backgroundColor: '#29aef8',
                                borderRadius: '20px',
                                color: 'white',
                                textTransform: 'none',
                                padding: '4px 16px',
                                height: '32px',
                                fontSize: '0.85rem',
                                '&:hover': {
                                    backgroundColor: '#1e88e5',
                                },
                            }}
                        >
                            Dismiss
                        </Button>
                    )}
                </Box>
            </Box>

            {!allStepsCompleted && (
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '100%',
                    padding: '0 0',
                    boxSizing: 'border-box',
                    overflow: 'visible'
                }}>
                    {/* Left Gradient Overlay */}
                    {scrollPosition > 0 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '0',
                                top: '0',
                                bottom: '0',
                                width: '60px',
                                background: 'linear-gradient(to right, rgba(255,255,255,0.9), transparent)',
                                zIndex: 15,
                                pointerEvents: 'none'
                            }}
                        />
                    )}

                    {/* Left Arrow */}
                    {scrollPosition > 0 && (
                        <IconButton
                            onClick={scrollLeft}
                            sx={{
                                position: 'absolute',
                                left: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 20,
                                width: '36px',
                                height: '36px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                // border: '2px solid #007bff', // Blue border for visibility
                                '&:hover': {
                                    backgroundColor: '#f8f8f8',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                    transform: 'translateY(-50%) scale(1.05)'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ArrowBackIos sx={{ fontSize: '16px', color: '#333' }} />
                        </IconButton>
                    )}

                    {/* Right Gradient Overlay */}
                    {canScrollRight && (
                        <Box
                            sx={{
                                position: 'absolute',
                                right: '0',
                                top: '0',
                                bottom: '0',
                                width: '60px',
                                background: 'linear-gradient(to left, rgba(255,255,255,0.9), transparent)',
                                zIndex: 15,
                                pointerEvents: 'none'
                            }}
                        />
                    )}

                    {/* Right Arrow */}
                    {canScrollRight && (
                        <IconButton
                            onClick={scrollRight}
                            sx={{
                                position: 'absolute',
                                right: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 20,
                                width: '36px',
                                height: '36px',
                                backgroundColor: 'white', // Always white for visibility
                                borderRadius: '50%',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                // border: '2px solid #007bff', // Blue border for visibility
                                '&:hover': {
                                    backgroundColor: '#f8f8f8',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                    transform: 'translateY(-50%) scale(1.05)'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ArrowForwardIos sx={{ fontSize: '16px', color: '#333' }} />
                        </IconButton>
                    )}

                    {/* Scrollable Container */}
                    <Box
                        ref={scrollContainerRef}
                        sx={{
                            display: 'flex',
                            gap: '16px',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            scrollBehavior: 'smooth',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            scrollbarWidth: 'none', // Firefox
                            msOverflowStyle: 'none' // IE/Edge
                        }}
                    >
                        {progressSteps.map((step, index) => (
                            <Box
                                key={index}
                                sx={{
                                    minWidth: step.width,
                                    flexShrink: 0
                                }}
                            >
                                <TeamProgressBox
                                    title={step.title}
                                    description={step.description}
                                    completed={step.completed}
                                    onClick={step.onClick}
                                    width={step.width}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default TeamProgressUI;
